import { todayLocal } from '@/lib/dates'
import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable, PageHeader, StatusBadge } from '@/components/common'
import { usePermission } from '@/hooks/usePermission'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Select } from '@/components/ui/select'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useDepartments } from '@/features/org'
import { useLookup } from '@/hooks/useLookup'
import { useDailyAttendance } from '../api'
import type { DailyRow } from '../api'

const TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  PRESENT: 'success',
  LATE: 'warning',
  HALF_DAY: 'warning',
  ON_LEAVE: 'info',
  WEEK_OFF: 'neutral',
  HOLIDAY: 'neutral',
  ABSENT: 'danger',
  NOT_PROCESSED: 'neutral',
}

const LABEL: Record<string, string> = {
  PRESENT: 'Present',
  LATE: 'Late',
  HALF_DAY: 'Half day',
  ON_LEAVE: 'On leave',
  WEEK_OFF: 'Week off',
  HOLIDAY: 'Holiday',
  ABSENT: 'Absent',
  NOT_PROCESSED: 'Not processed',
}

/** One day, everybody: the screen an attendance clerk actually opens. */
export function DailyAttendancePage() {
  useBreadcrumb()
  const [date, setDate] = useState(todayLocal())
  const [departmentId, setDepartmentId] = useState('')
  const [entityId, setEntityId] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)
  const [search, setSearch] = useState('')

  const rows = useDailyAttendance({ date, departmentId, entityId, q: search, page, size })
  const departments = useDepartments()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')

  const columns = useMemo<ColumnDef<DailyRow, unknown>[]>(() => [
    { id: 'employeeCode', header: 'Code', accessorFn: (row) => row.employeeCode },
    { id: 'employeeName', header: 'Name', accessorFn: (row) => row.employeeName },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={TONE[row.original.status] ?? 'neutral'}
          label={LABEL[row.original.status] ?? row.original.status}
        />
      ),
    },
    {
      id: 'firstIn',
      header: 'In',
      accessorFn: (row) => row.firstIn,
      cell: ({ row }) => row.original.firstIn
        ? new Date(row.original.firstIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—',
    },
    {
      id: 'lastOut',
      header: 'Out',
      accessorFn: (row) => row.lastOut,
      cell: ({ row }) => row.original.lastOut
        ? new Date(row.original.lastOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—',
    },
    {
      id: 'workedMinutes',
      header: 'Worked',
      accessorFn: (row) => row.workedMinutes,
      cell: ({ row }) => row.original.recorded
        ? `${(row.original.workedMinutes / 60).toFixed(1)}h`
        : <span className="text-slate-500">—</span>,
    },
    {
      id: 'lateMinutes',
      header: 'Late',
      accessorFn: (row) => row.lateMinutes,
      cell: ({ row }) => row.original.lateMinutes > 0
        ? <span className="text-amber-700">{row.original.lateMinutes} min</span> : '—',
    },
    {
      id: 'overtimeMinutes',
      header: 'Overtime',
      accessorFn: (row) => row.overtimeMinutes,
      cell: ({ row }) => row.original.overtimeMinutes > 0
        ? `${(row.original.overtimeMinutes / 60).toFixed(1)}h` : '—',
    },
  ], [])

  // What the screen can honestly claim depends on what the caller holds: the backend narrows
  // the rows to the reporting line without this permission, and a subtitle that says
  // "everybody" over a filtered grid is a lie the user cannot see.
  const seesEverybody = usePermission('attendance:read-all')

  return (
    <div className="space-y-4">
      <PageHeader
        title="Daily attendance"
        subtitle={seesEverybody
          ? 'One day, everybody, grouped by where they were on that day.'
          : 'One day: you and the people who reported to you on it.'}
      />

      {!seesEverybody && (
        <Card className="p-4 text-sm text-slate-700">
          <p>
            This grid shows your own attendance and your reporting line as it stood on the day
            you are looking at — not the whole company. Seeing everybody needs
            {' '}<span className="font-mono">attendance:read-all</span>.
          </p>
        </Card>
      )}

      <Card className="p-4 text-sm text-slate-700">
        <p>
          "Not processed" is not "absent": it means nothing has been recorded for that person on
          that day, and the difference decides whether somebody is chased for a punch or for an
          explanation. Departments are resolved as of the day being looked at, so a historical
          grid matches the payroll that came out of it.
        </p>
      </Card>

      <DataTable<DailyRow>
        columns={columns}
        data={rows.data}
        isLoading={rows.isPending}
        error={rows.error}
        onRetry={() => void rows.refetch()}
        page={page}
        size={size}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        emptyTitle="Nobody matches"
        emptyDescription="Change the day, the entity or the department."
        toolbar={
          <>
            <DatePicker
              aria-label="Day"
              id="daily-date" value={date}
              onChange={(event) => { setDate(event.target.value); setPage(0) }}
            />
            <Select
              aria-label="Entity"
              id="daily-entity"
              value={entityId}
              onChange={(event) => { setEntityId(event.target.value); setPage(0) }}
            >
              <option value="">Every entity</option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
            <Select
              aria-label="Department"
              id="daily-department"
              value={departmentId}
              onChange={(event) => { setDepartmentId(event.target.value); setPage(0) }}
            >
              <option value="">Every department</option>
              {departments.data?.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </Select>
          </>
        }
      />
    </div>
  )
}
